import React, { useCallback, useRef, useState } from 'react'
import { useMutation } from '@apollo/client';
import { IOrg } from '../../../types/org';
import Info from '../../common/info';
import { GET_INFOGO_ASSOCIATED_ORGS, GET_INFOGO_HEADER_ORGS, GET_INFOGO_POSITIONS, SAVE_ORG_CONFIG, } from '../../../graphql/infogo-gql';
import { CheckIcon, DeleteIcon, SaveIcon } from '../../common/icons';
import { notifySuccess } from '../../../services/notification';
import SpinnerOverlay from '../../common/spinner/spinner';
import CollapsablePanel from '../../common/collapsable-panel/collapsable-panel';
import PositionConfig, { PositionConfigHandler } from './positions/position-config';
import './infogo-config.scss';
import Spacer from '../../common/spacer';
import AssociatedOrgs, { AssociatedOrgsHandler } from './associated-orgs/associated-orgs';
import HeaderOrgs, { HeaderOrgsHandler } from './header-orgs/header-orgs';
import { CategoryType } from '../../../types/constants';
import confirm from '../../common/confirm';
import useAuth from '../../../hook/useAuth';
import Button from '../../common/button';

type InfoGoConfigProps = {
    org?: IOrg;
}

const InfoGoConfig = (props: InfoGoConfigProps) => {
    const { org } = props
    const { isFrenchTranslator } = useAuth()
    const [ saveOrgConfig, { loading: loadingSave }] = useMutation(SAVE_ORG_CONFIG)
    const positionRef = useRef<PositionConfigHandler>(null)
    const assOrgRef = useRef<AssociatedOrgsHandler>(null)
    const headerRef = useRef<HeaderOrgsHandler>(null)
    const orgHeaderTitle = org?.details?.category?.type === CategoryType.ORGANIZATION? 'Headers & Organizations' : 'Sub Groups'

    const [ toggleChange, setToggleChange ] = useState<boolean>(false)

    const onReset = () => {
        if (positionRef.current && positionRef.current.hasChange()) {
            positionRef.current.reset()
        }
        if (assOrgRef.current && assOrgRef.current.hasChange()) {
            assOrgRef.current.reset()
        }
        if (headerRef.current && headerRef.current.hasChange()) {
            headerRef.current.reset()
        }
    }

    const onSave = () => {
        if (headerRef.current && headerRef.current.hasNewSuppress()
                || positionRef.current && positionRef.current.hasNewSuppress()
                || assOrgRef.current && assOrgRef.current.hasNewSuppress()) {
            confirm({
                title: `Suppress Data?`,
                content: `The suppressed data will not be published on INFO-GO sites.`,
                okText: <><CheckIcon/> Confirm</>
            }).then(() => {
                _onSave()
            })
        } else {
            _onSave()
        }
    }

    const _onSave = () => {
        let positionConfig = positionRef.current && positionRef.current.hasChange() ? positionRef.current.getPositionConfig() : null
        let assOrgConfig = assOrgRef.current && assOrgRef.current.hasChange() ? assOrgRef.current.getAssociatedOrgsConfig() : null
        let headerOrgConfig = null
        let parentChanges = null
        if (headerRef.current && headerRef.current.hasChange()) {
            headerOrgConfig = headerRef.current.getHeaderOrgsConfig()
            parentChanges = headerRef.current.getParentChanges()
        }

        saveOrgConfig({
            variables: {
                orgId: org?.id,
                input: {
                    positionConfig: positionConfig,
                    associatedOrgConfig: assOrgConfig,
                    headerOrgConfig: headerOrgConfig,
                    parentChanges: parentChanges
                }
            },
            refetchQueries: getRefetchQueries(),
            awaitRefetchQueries: true,
            onCompleted: (data) => {
                setTimeout(()=>onToggleChange())
                data && notifySuccess()
            }
        })
    }

    const getRefetchQueries = () => {
        let result = []
        if (positionRef.current && positionRef.current.hasChange()) {
            result.push({
                query: GET_INFOGO_POSITIONS,
                variables: { orgId: org?.id }
            })
        }
        if (assOrgRef.current && assOrgRef.current.hasChange()) {
            result.push({
                query: GET_INFOGO_ASSOCIATED_ORGS,
                variables: { orgId: org?.id }
            })
        }
        if (headerRef.current && headerRef.current.hasChange()) {
            result.push({
                query: GET_INFOGO_HEADER_ORGS,
                variables: { orgId: org?.id }
            })
        }
        return result
    }

    const hasChange = () => {
        return positionRef.current && positionRef.current.hasChange()
            || assOrgRef.current && assOrgRef.current.hasChange()
            || headerRef.current && headerRef.current.hasChange()
    }

    const onToggleChange = useCallback(() => {
        setToggleChange((toggle: boolean) => !toggle)
    }, [toggleChange])

    //console.log("render infogo config")

    return (
        <div>
            {loadingSave && <SpinnerOverlay />}

            {!isFrenchTranslator && <div className="pb-3">
                <Button color='primary' onClick={onSave} style={{ marginRight: '1rem' }} disabled={!hasChange()}>
                    <SaveIcon /> Save Changes
                </Button>
                <Button color='danger' onClick={onReset} disabled={!hasChange()}>
                    <DeleteIcon /> Discard Changes
                </Button>
            </div>}

            {!isFrenchTranslator && <Info>Drag and drop entries in the tables to re-order, then click the <strong><i>SAVE</i></strong> button to save those changes.</Info>}
            <Spacer height="0.5rem"/>

            
            <>
            <CollapsablePanel headTitle="Positions & Roles" initCollapse={false} helpUrl="">
                <PositionConfig ref={positionRef} org={org} onChange={onToggleChange}/>
            </CollapsablePanel>
            <Spacer/>
            </>
            

            <CollapsablePanel headTitle={orgHeaderTitle} initCollapse={false} helpUrl="">
                <HeaderOrgs ref={headerRef} org={org} onChange={onToggleChange}></HeaderOrgs>
            </CollapsablePanel>

            <Spacer/>
            {org?.details?.category?.type === CategoryType.ORGANIZATION && <CollapsablePanel headTitle="Associated Organizations" initCollapse={false} helpUrl="">
                <AssociatedOrgs ref={assOrgRef} org={org} onChange={onToggleChange}></AssociatedOrgs>
            </CollapsablePanel>
            }

        </div>
    )
}

export default InfoGoConfig
